import React, { useState } from 'react';
import { 
  Code, 
  GitBranch, 
  Zap, 
  Target, 
  Activity,
  Clock,
  CheckCircle,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Users,
  Brain,
  Shield,
  Cpu,
  Database,
  BarChart3,
  Timer,
  Gauge,
  Award,
  Bug,
  Rocket,
  Eye,
  Settings,
  FileText,
  Lock,
  Layers,
  GitCommit,
  PlayCircle,
  PauseCircle,
  XCircle,
  ArrowRight,
  Lightbulb,
  Heart,
  Coffee,
  Workflow,
  Search,
  Filter
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, RadialBarChart, RadialBar } from 'recharts';

const EngineeringDashboard: React.FC = () => {
  const [selectedTimeframe, setSelectedTimeframe] = useState('sprint');
  const [selectedTeam, setSelectedTeam] = useState('all');
  const [activeSection, setActiveSection] = useState('overview');

  // 1. Strategy & Value Alignment Data
  const strategicAlignmentData = [
    { initiative: 'AI Personalisation', strategic_goal: 'Customer Experience', alignment: 95, value_delivered: 2.4, status: 'active' },
    { initiative: 'Mobile Optimization', strategic_goal: 'Revenue Growth', alignment: 88, value_delivered: 1.8, status: 'active' },
    { initiative: 'Platform Modernization', strategic_goal: 'Operational Efficiency', alignment: 92, value_delivered: 3.2, status: 'active' },
    { initiative: 'Security Hardening', strategic_goal: 'Risk Mitigation', alignment: 85, value_delivered: 1.2, status: 'completed' },
    { initiative: 'API Gateway', strategic_goal: 'Developer Experience', alignment: 78, value_delivered: 0.8, status: 'discovery' },
  ];

  const workAlignmentData = [
    { category: 'Strategic Bets', percentage: 65, value: '£8.2M', color: '#10B981' },
    { category: 'Revenue Targets', percentage: 22, value: '£3.1M', color: '#3B82F6' },
    { category: 'Retention/CX', percentage: 18, value: '£2.4M', color: '#8B5CF6' },
    { category: 'Technical Debt', percentage: 12, value: '£1.5M', color: '#F59E0B' },
    { category: 'Maintenance', percentage: 8, value: '£0.9M', color: '#6B7280' },
  ];

  const valueImpactData = [
    { feature: 'AI Recommendations', cx_impact: 23, nps_impact: 12, revenue_impact: 340000, efficiency_gain: 18 },
    { feature: 'Mobile Checkout', cx_impact: 31, nps_impact: 8, revenue_impact: 280000, efficiency_gain: 0 },
    { feature: 'Real-time Analytics', cx_impact: 15, nps_impact: 5, revenue_impact: 120000, efficiency_gain: 35 },
    { feature: 'Auto-scaling', cx_impact: 8, nps_impact: 3, revenue_impact: 50000, efficiency_gain: 42 },
  ];

  // 2. Product & Delivery Flow Data
  const deliveryFlowData = [
    { stage: 'Discovery', count: 12, percentage: 15, avg_time: 3.2 },
    { stage: 'Build', count: 28, percentage: 35, avg_time: 8.5 },
    { stage: 'Testing', count: 18, percentage: 22, avg_time: 2.1 },
    { stage: 'Awaiting Release', count: 8, percentage: 10, avg_time: 1.2 },
    { stage: 'Released', count: 14, percentage: 18, avg_time: 0 },
  ];

  const cycleTimeData = [
    { week: 'W1', cycle_time: 12.5, deploy_freq: 8, change_failure: 6 },
    { week: 'W2', cycle_time: 11.2, deploy_freq: 12, change_failure: 4 },
    { week: 'W3', cycle_time: 9.8, deploy_freq: 15, change_failure: 3 },
    { week: 'W4', cycle_time: 8.5, deploy_freq: 18, change_failure: 2 },
    { week: 'W5', cycle_time: 7.2, deploy_freq: 22, change_failure: 1 },
    { week: 'W6', cycle_time: 6.1, deploy_freq: 25, change_failure: 1 },
  ];

  const experimentStatusData = [
    { status: 'Validated', count: 15, color: '#10B981' },
    { status: 'Running', count: 8, color: '#3B82F6' },
    { status: 'Invalidated', count: 5, color: '#EF4444' },
    { status: 'Paused', count: 3, color: '#F59E0B' },
  ];

  // 3. Code & System Health Data
  const technicalDebtData = [
    { module: 'User Authentication', complexity: 85, age_months: 24, debt_score: 92, priority: 'high' },
    { module: 'Payment Processing', complexity: 78, age_months: 18, debt_score: 85, priority: 'high' },
    { module: 'Notification Service', complexity: 65, age_months: 12, debt_score: 72, priority: 'medium' },
    { module: 'Analytics Engine', complexity: 58, age_months: 8, debt_score: 65, priority: 'medium' },
    { module: 'File Upload', complexity: 45, age_months: 6, debt_score: 52, priority: 'low' },
  ];

  const codeQualityData = [
    { metric: 'Test Coverage', current: 87, target: 85, trend: 'up' },
    { metric: 'Code Duplication', current: 8, target: 10, trend: 'down' },
    { metric: 'Complexity Score', current: 6.2, target: 7.0, trend: 'stable' },
    { metric: 'Security Score', current: 94, target: 90, trend: 'up' },
  ];

  const systemReliabilityData = [
    { service: 'API Gateway', slo: 99.9, actual: 99.95, error_rate: 0.01, latency: 85 },
    { service: 'User Service', slo: 99.5, actual: 99.8, error_rate: 0.02, latency: 120 },
    { service: 'Payment Service', slo: 99.9, actual: 99.92, error_rate: 0.005, latency: 95 },
    { service: 'Analytics Service', slo: 99.0, actual: 99.1, error_rate: 0.08, latency: 180 },
  ];

  // 4. Team Health & Productivity Data
  const teamHealthData = [
    { team: 'Platform Team', focus_time: 82, wip_per_person: 2.1, pr_review_time: 4.2, satisfaction: 8.5, golden_path: 95 },
    { team: 'Product Team', focus_time: 75, wip_per_person: 2.8, pr_review_time: 6.1, satisfaction: 7.8, golden_path: 88 },
    { team: 'Data Team', focus_time: 88, wip_per_person: 1.9, pr_review_time: 3.5, satisfaction: 9.1, golden_path: 92 },
    { team: 'Mobile Team', focus_time: 71, wip_per_person: 3.2, pr_review_time: 5.8, satisfaction: 7.2, golden_path: 85 },
  ];

  const developerProductivityData = [
    { week: 'W1', focus_time: 72, cognitive_load: 6.2, satisfaction: 7.8 },
    { week: 'W2', focus_time: 75, cognitive_load: 5.9, satisfaction: 8.1 },
    { week: 'W3', focus_time: 78, cognitive_load: 5.5, satisfaction: 8.3 },
    { week: 'W4', focus_time: 82, cognitive_load: 5.2, satisfaction: 8.6 },
    { week: 'W5', focus_time: 85, cognitive_load: 4.8, satisfaction: 8.8 },
    { week: 'W6', focus_time: 88, cognitive_load: 4.5, satisfaction: 9.0 },
  ];

  // 5. Security & Compliance Data
  const securityMetricsData = [
    { category: 'Critical Vulnerabilities', open: 2, closed_this_week: 5, trend: 'improving' },
    { category: 'High Vulnerabilities', open: 8, closed_this_week: 12, trend: 'improving' },
    { category: 'Secrets Exposed', open: 0, closed_this_week: 3, trend: 'stable' },
    { category: 'Policy Violations', open: 4, closed_this_week: 7, trend: 'improving' },
  ];

  const aiGovernanceData = [
    { intervention: 'Risky Deployment Blocked', count: 8, savings: 45000, impact: 'high' },
    { intervention: 'Security Scan Auto-fix', count: 23, savings: 12000, impact: 'medium' },
    { intervention: 'Code Quality Gate', count: 15, savings: 8000, impact: 'medium' },
    { intervention: 'Compliance Check', count: 31, savings: 18000, impact: 'high' },
  ];

  // 6. AI Enablement Data
  const aiUsageData = [
    { category: 'Code Generation', ai_assisted: 68, time_saved: 45, accuracy: 92 },
    { category: 'Test Generation', ai_assisted: 82, time_saved: 32, accuracy: 88 },
    { category: 'Documentation', ai_assisted: 75, time_saved: 28, accuracy: 95 },
    { category: 'Code Review', ai_assisted: 45, time_saved: 18, accuracy: 85 },
    { category: 'Bug Detection', ai_assisted: 58, time_saved: 22, accuracy: 91 },
  ];

  // 7. Impact & Learning Data
  const learningMetricsData = [
    { quarter: 'Q1', hypotheses_tested: 24, success_rate: 68, learning_velocity: 72 },
    { quarter: 'Q2', hypotheses_tested: 31, success_rate: 71, learning_velocity: 78 },
    { quarter: 'Q3', hypotheses_tested: 28, success_rate: 74, learning_velocity: 82 },
    { quarter: 'Q4', hypotheses_tested: 35, success_rate: 76, learning_velocity: 85 },
  ];

  const continuousImprovementData = [
    { improvement: 'Automated Testing Pipeline', status: 'completed', impact: 'high', time_saved: 120 },
    { improvement: 'Code Review Automation', status: 'in_progress', impact: 'medium', time_saved: 80 },
    { improvement: 'Deployment Standardization', status: 'completed', impact: 'high', time_saved: 200 },
    { improvement: 'Monitoring Enhancement', status: 'planned', impact: 'medium', time_saved: 60 },
  ];

  // Key Engineering Metrics
  const engineeringMetrics = [
    {
      id: 'strategic_alignment',
      name: 'Strategic Alignment',
      value: 88,
      change: 5.1,
      positive: true,
      description: '% of work tied to strategic KPIs',
      target: 85,
      unit: '%',
      detail: '£13.4M value delivered'
    },
    {
      id: 'delivery_velocity',
      name: 'Delivery Velocity',
      value: 6.1,
      change: -51.2,
      positive: true,
      description: 'Average cycle time (days)',
      target: 7.0,
      unit: 'days',
      detail: '25 deploys/week'
    },
    {
      id: 'system_health',
      name: 'System Health',
      value: 99.8,
      change: 0.1,
      positive: true,
      description: 'Overall system reliability',
      target: 99.5,
      unit: '%',
      detail: '0.01% error rate'
    },
    {
      id: 'team_satisfaction',
      name: 'Team Health',
      value: 8.6,
      change: 0.8,
      positive: true,
      description: 'Developer satisfaction score',
      target: 8.0,
      unit: '/10',
      detail: '85% focus time'
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-500';
      case 'completed': return 'bg-blue-500';
      case 'discovery': return 'bg-yellow-500';
      case 'paused': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'text-red-400 bg-red-900';
      case 'medium': return 'text-yellow-400 bg-yellow-900';
      case 'low': return 'text-green-400 bg-green-900';
      default: return 'text-gray-400 bg-gray-900';
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up': return <TrendingUp className="w-4 h-4 text-green-400" />;
      case 'down': return <TrendingDown className="w-4 h-4 text-red-400" />;
      case 'stable': return <Activity className="w-4 h-4 text-gray-400" />;
      default: return <Activity className="w-4 h-4 text-gray-400" />;
    }
  };

  const sections = [
    { id: 'overview', name: 'Overview', icon: BarChart3 },
    { id: 'strategy', name: 'Strategy & Value', icon: Target },
    { id: 'delivery', name: 'Delivery Flow', icon: Workflow },
    { id: 'health', name: 'Code & System Health', icon: Activity },
    { id: 'team', name: 'Team Productivity', icon: Users },
    { id: 'security', name: 'Security & Governance', icon: Shield },
    { id: 'ai', name: 'AI Enablement', icon: Brain },
    { id: 'learning', name: 'Impact & Learning', icon: Lightbulb },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center space-x-3">
            <Code className="w-8 h-8 text-indigo-400" />
            <span>Engineering Excellence Dashboard</span>
          </h1>
          <p className="text-gray-400">Comprehensive engineering metrics across strategy, delivery, health, and team productivity</p>
        </div>
        <div className="flex items-center space-x-3 mt-4 lg:mt-0">
          <select
            value={selectedTimeframe}
            onChange={(e) => setSelectedTimeframe(e.target.value)}
            className="bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-indigo-500 focus:outline-none"
          >
            <option value="sprint">Current Sprint</option>
            <option value="weekly">Weekly View</option>
            <option value="monthly">Monthly View</option>
            <option value="quarterly">Quarterly View</option>
          </select>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-indigo-500 rounded-full animate-pulse"></div>
            <span className="text-sm text-gray-300">Live Engineering Data</span>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-gray-700 pb-4">
        {sections.map((section) => (
          <button
            key={section.id}
            onClick={() => setActiveSection(section.id)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              activeSection === section.id
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            <section.icon className="w-4 h-4" />
            <span className="text-sm">{section.name}</span>
          </button>
        ))}
      </div>

      {/* Overview Section */}
      {activeSection === 'overview' && (
        <div className="space-y-6">
          {/* Key Engineering Metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {engineeringMetrics.map((metric) => (
              <div key={metric.id} className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium text-gray-300">{metric.name}</h3>
                  {metric.positive ? (
                    <TrendingUp className="w-5 h-5 text-green-500" />
                  ) : (
                    <TrendingDown className="w-5 h-5 text-red-500" />
                  )}
                </div>
                <div className="flex items-baseline space-x-2 mb-2">
                  <span className="text-2xl font-bold text-white">{metric.value}</span>
                  <span className="text-sm text-gray-400">{metric.unit}</span>
                </div>
                <div className="flex items-center justify-between mb-3">
                  <span className={`text-sm ${metric.positive ? 'text-green-500' : 'text-red-500'}`}>
                    {metric.positive ? '+' : ''}{metric.change}%
                  </span>
                  <span className="text-xs text-gray-400">{metric.detail}</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full ${metric.value >= metric.target ? 'bg-green-500' : 'bg-yellow-500'}`}
                    style={{ width: `${Math.min((metric.value / (metric.target * 1.2)) * 100, 100)}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>

          {/* Quick Status Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <div className="flex items-center space-x-3 mb-4">
                <Target className="w-6 h-6 text-green-400" />
                <h3 className="text-lg font-semibold text-white">Strategic Work</h3>
              </div>
              <div className="text-3xl font-bold text-white mb-2">88%</div>
              <div className="text-sm text-green-400">+5.1% alignment to KPIs</div>
            </div>

            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <div className="flex items-center space-x-3 mb-4">
                <Rocket className="w-6 h-6 text-blue-400" />
                <h3 className="text-lg font-semibold text-white">Deployment Freq</h3>
              </div>
              <div className="text-3xl font-bold text-white mb-2">25</div>
              <div className="text-sm text-blue-400">deploys/week (+39%)</div>
            </div>

            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <div className="flex items-center space-x-3 mb-4">
                <Activity className="w-6 h-6 text-purple-400" />
                <h3 className="text-lg font-semibold text-white">System Health</h3>
              </div>
              <div className="text-3xl font-bold text-white mb-2">99.8%</div>
              <div className="text-sm text-purple-400">uptime (+0.1%)</div>
            </div>

            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <div className="flex items-center space-x-3 mb-4">
                <Heart className="w-6 h-6 text-pink-400" />
                <h3 className="text-lg font-semibold text-white">Team Health</h3>
              </div>
              <div className="text-3xl font-bold text-white mb-2">8.6</div>
              <div className="text-sm text-pink-400">satisfaction (+0.8)</div>
            </div>
          </div>
        </div>
      )}

      {/* Strategy & Value Alignment Section */}
      {activeSection === 'strategy' && (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-white flex items-center space-x-3">
            <Target className="w-6 h-6 text-green-400" />
            <span>Strategy & Value Alignment</span>
          </h2>

          {/* Strategic Initiatives Mapping */}
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-4">Active Initiatives → Strategic Goals</h3>
            <div className="space-y-4">
              {strategicAlignmentData.map((initiative, index) => (
                <div key={index} className="bg-gray-700 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className={`w-3 h-3 rounded-full ${getStatusColor(initiative.status)}`}></div>
                      <h4 className="text-white font-medium">{initiative.initiative}</h4>
                      <ArrowRight className="w-4 h-4 text-gray-400" />
                      <span className="text-indigo-400">{initiative.strategic_goal}</span>
                    </div>
                    <div className="text-green-400 font-bold">£{initiative.value_delivered}M</div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="flex-1 bg-gray-600 rounded-full h-2">
                      <div 
                        className="bg-green-500 h-2 rounded-full" 
                        style={{ width: `${initiative.alignment}%` }}
                      ></div>
                    </div>
                    <span className="text-sm text-white font-medium">{initiative.alignment}% aligned</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Work Alignment Breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4">Work Allocation by Category</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={workAlignmentData}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    dataKey="percentage"
                    label={({ category, percentage }) => `${category}: ${percentage}%`}
                    labelLine={false}
                  >
                    {workAlignmentData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1F2937',
                      border: '1px solid #374151',
                      borderRadius: '8px',
                      color: '#F9FAFB'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4">Value Impact Tracking</h3>
              <div className="space-y-4">
                {valueImpactData.map((feature, index) => (
                  <div key={index} className="bg-gray-700 rounded-lg p-4">
                    <h4 className="text-white font-medium mb-3">{feature.feature}</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <div className="text-gray-400">CX Impact</div>
                        <div className="text-green-400 font-bold">+{feature.cx_impact}%</div>
                      </div>
                      <div>
                        <div className="text-gray-400">NPS Impact</div>
                        <div className="text-blue-400 font-bold">+{feature.nps_impact}</div>
                      </div>
                      <div>
                        <div className="text-gray-400">Revenue Impact</div>
                        <div className="text-yellow-400 font-bold">£{(feature.revenue_impact / 1000).toFixed(0)}K</div>
                      </div>
                      <div>
                        <div className="text-gray-400">Efficiency Gain</div>
                        <div className="text-purple-400 font-bold">{feature.efficiency_gain}%</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delivery Flow Section */}
      {activeSection === 'delivery' && (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-white flex items-center space-x-3">
            <Workflow className="w-6 h-6 text-blue-400" />
            <span>Product & Delivery Flow</span>
          </h2>

          {/* Cycle Time Trends */}
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-4">Cycle Time & Deployment Trends</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={cycleTimeData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="week" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1F2937',
                    border: '1px solid #374151',
                    borderRadius: '8px',
                    color: '#F9FAFB'
                  }}
                />
                <Line type="monotone" dataKey="cycle_time" stroke="#EF4444" strokeWidth={3} name="Cycle Time (days)" />
                <Line type="monotone" dataKey="deploy_freq" stroke="#10B981" strokeWidth={2} name="Deploy Frequency" />
                <Line type="monotone" dataKey="change_failure" stroke="#F59E0B" strokeWidth={2} name="Change Failure %" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Backlog Flow */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4">Backlog Distribution</h3>
              <div className="space-y-4">
                {deliveryFlowData.map((stage, index) => (
                  <div key={index} className="bg-gray-700 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-white font-medium">{stage.stage}</h4>
                      <div className="flex items-center space-x-2">
                        <span className="text-white font-bold">{stage.count}</span>
                        <span className="text-gray-400">({stage.percentage}%)</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="flex-1 bg-gray-600 rounded-full h-2">
                        <div 
                          className="bg-blue-500 h-2 rounded-full" 
                          style={{ width: `${stage.percentage}%` }}
                        ></div>
                      </div>
                      {stage.avg_time > 0 && (
                        <span className="text-sm text-gray-400">{stage.avg_time}d avg</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4">Experiment Status</h3>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={experimentStatusData}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="count"
                    label={({ status, count }) => `${status}: ${count}`}
                    labelLine={false}
                  >
                    {experimentStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1F2937',
                      border: '1px solid #374151',
                      borderRadius: '8px',
                      color: '#F9FAFB'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* DORA Metrics Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 text-center">
              <Timer className="w-8 h-8 text-blue-400 mx-auto mb-3" />
              <div className="text-2xl font-bold text-white mb-1">6.1 days</div>
              <div className="text-sm text-gray-400">Lead Time</div>
              <div className="text-xs text-green-400 mt-1">-51% improvement</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 text-center">
              <Rocket className="w-8 h-8 text-green-400 mx-auto mb-3" />
              <div className="text-2xl font-bold text-white mb-1">25/week</div>
              <div className="text-sm text-gray-400">Deploy Frequency</div>
              <div className="text-xs text-green-400 mt-1">Elite Performer</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 text-center">
              <CheckCircle className="w-8 h-8 text-purple-400 mx-auto mb-3" />
              <div className="text-2xl font-bold text-white mb-1">1%</div>
              <div className="text-sm text-gray-400">Change Failure Rate</div>
              <div className="text-xs text-green-400 mt-1">-83% reduction</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 text-center">
              <Clock className="w-8 h-8 text-yellow-400 mx-auto mb-3" />
              <div className="text-2xl font-bold text-white mb-1">0.2h</div>
              <div className="text-sm text-gray-400">MTTR</div>
              <div className="text-xs text-green-400 mt-1">Elite Performer</div>
            </div>
          </div>
        </div>
      )}

      {/* Code & System Health Section */}
      {activeSection === 'health' && (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-white flex items-center space-x-3">
            <Activity className="w-6 h-6 text-purple-400" />
            <span>Code & System Health</span>
          </h2>

          {/* Technical Debt Hotspots */}
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-4">Technical Debt Hotspots</h3>
            <div className="space-y-4">
              {technicalDebtData.map((module, index) => (
                <div key={index} className="bg-gray-700 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-white font-medium">{module.module}</h4>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 rounded-full text-xs ${getPriorityColor(module.priority)}`}>
                        {module.priority} priority
                      </span>
                      <span className="text-red-400 font-bold">{module.debt_score}</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-gray-400">Complexity</div>
                      <div className="text-white font-medium">{module.complexity}</div>
                    </div>
                    <div>
                      <div className="text-gray-400">Age</div>
                      <div className="text-white font-medium">{module.age_months} months</div>
                    </div>
                    <div>
                      <div className="text-gray-400">Debt Score</div>
                      <div className="w-full bg-gray-600 rounded-full h-2 mt-1">
                        <div 
                          className={`h-2 rounded-full ${module.debt_score > 80 ? 'bg-red-500' : module.debt_score > 60 ? 'bg-yellow-500' : 'bg-green-500'}`}
                          style={{ width: `${module.debt_score}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Code Quality Metrics */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4">Code Quality Metrics</h3>
              <div className="space-y-4">
                {codeQualityData.map((metric, index) => (
                  <div key={index} className="bg-gray-700 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-white font-medium">{metric.metric}</h4>
                      <div className="flex items-center space-x-2">
                        {getTrendIcon(metric.trend)}
                        <span className="text-white font-bold">{metric.current}</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-400">Target: {metric.target}</span>
                      <span className={`${metric.current >= metric.target ? 'text-green-400' : 'text-yellow-400'}`}>
                        {metric.current >= metric.target ? 'Above target' : 'Below target'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4">System Reliability</h3>
              <div className="space-y-4">
                {systemReliabilityData.map((service, index) => (
                  <div key={index} className="bg-gray-700 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-white font-medium">{service.service}</h4>
                      <div className={`w-3 h-3 rounded-full ${service.actual >= service.slo ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    </div>
                    <div className="grid grid-cols-3 gap-3 text-sm">
                      <div>
                        <div className="text-gray-400">SLO</div>
                        <div className="text-white font-medium">{service.slo}%</div>
                      </div>
                      <div>
                        <div className="text-gray-400">Actual</div>
                        <div className={`font-medium ${service.actual >= service.slo ? 'text-green-400' : 'text-red-400'}`}>
                          {service.actual}%
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-400">Latency</div>
                        <div className="text-white font-medium">{service.latency}ms</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Team Health & Productivity Section */}
      {activeSection === 'team' && (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-white flex items-center space-x-3">
            <Users className="w-6 h-6 text-pink-400" />
            <span>Team Health & Productivity</span>
          </h2>

          {/* Developer Productivity Trends */}
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-4">Developer Productivity Trends</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={developerProductivityData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="week" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1F2937',
                    border: '1px solid #374151',
                    borderRadius: '8px',
                    color: '#F9FAFB'
                  }}
                />
                <Line type="monotone" dataKey="focus_time" stroke="#10B981" strokeWidth={3} name="Focus Time %" />
                <Line type="monotone" dataKey="satisfaction" stroke="#3B82F6" strokeWidth={2} name="Satisfaction" />
                <Line type="monotone" dataKey="cognitive_load" stroke="#EF4444" strokeWidth={2} name="Cognitive Load" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Team Health Breakdown */}
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-4">Team Health Breakdown</h3>
            <div className="space-y-4">
              {teamHealthData.map((team, index) => (
                <div key={index} className="bg-gray-700 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-white font-medium">{team.team}</h4>
                    <div className="flex items-center space-x-2">
                      <Coffee className="w-4 h-4 text-yellow-400" />
                      <span className="text-white font-bold">{team.satisfaction}/10</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <div className="text-gray-400">Focus Time</div>
                      <div className="text-green-400 font-medium">{team.focus_time}%</div>
                    </div>
                    <div>
                      <div className="text-gray-400">WIP/Person</div>
                      <div className={`font-medium ${team.wip_per_person <= 2.5 ? 'text-green-400' : 'text-yellow-400'}`}>
                        {team.wip_per_person}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-400">PR Review Time</div>
                      <div className="text-blue-400 font-medium">{team.pr_review_time}h</div>
                    </div>
                    <div>
                      <div className="text-gray-400">Golden Path</div>
                      <div className="text-purple-400 font-medium">{team.golden_path}%</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Team Health Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 text-center">
              <Coffee className="w-8 h-8 text-yellow-400 mx-auto mb-3" />
              <div className="text-2xl font-bold text-white mb-1">8.6/10</div>
              <div className="text-sm text-gray-400">Average Satisfaction</div>
              <div className="text-xs text-green-400 mt-1">+0.8 improvement</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 text-center">
              <Eye className="w-8 h-8 text-blue-400 mx-auto mb-3" />
              <div className="text-2xl font-bold text-white mb-1">85%</div>
              <div className="text-sm text-gray-400">Focus Time</div>
              <div className="text-xs text-green-400 mt-1">+13% increase</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 text-center">
              <Workflow className="w-8 h-8 text-purple-400 mx-auto mb-3" />
              <div className="text-2xl font-bold text-white mb-1">90%</div>
              <div className="text-sm text-gray-400">Golden Path Adoption</div>
              <div className="text-xs text-green-400 mt-1">Platform accelerators</div>
            </div>
          </div>
        </div>
      )}

      {/* Security & Governance Section */}
      {activeSection === 'security' && (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-white flex items-center space-x-3">
            <Shield className="w-6 h-6 text-red-400" />
            <span>Security, Compliance & Governance</span>
          </h2>

          {/* Security Metrics */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4">Security Metrics</h3>
              <div className="space-y-4">
                {securityMetricsData.map((metric, index) => (
                  <div key={index} className="bg-gray-700 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-white font-medium">{metric.category}</h4>
                      <div className="flex items-center space-x-2">
                        {getTrendIcon(metric.trend)}
                        <span className={`font-bold ${metric.open === 0 ? 'text-green-400' : metric.open <= 5 ? 'text-yellow-400' : 'text-red-400'}`}>
                          {metric.open} open
                        </span>
                      </div>
                    </div>
                    <div className="text-sm text-gray-400">
                      {metric.closed_this_week} closed this week
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4">AI Governance Interventions</h3>
              <div className="space-y-4">
                {aiGovernanceData.map((intervention, index) => (
                  <div key={index} className="bg-gray-700 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-white font-medium">{intervention.intervention}</h4>
                      <div className="text-green-400 font-bold">£{(intervention.savings / 1000).toFixed(0)}K</div>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-400">{intervention.count} interventions</span>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        intervention.impact === 'high' ? 'bg-red-900 text-red-300' : 'bg-yellow-900 text-yellow-300'
                      }`}>
                        {intervention.impact} impact
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Compliance Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 text-center">
              <Lock className="w-8 h-8 text-green-400 mx-auto mb-3" />
              <div className="text-2xl font-bold text-white mb-1">0</div>
              <div className="text-sm text-gray-400">Critical Vulnerabilities</div>
              <div className="text-xs text-green-400 mt-1">5 closed this week</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 text-center">
              <Shield className="w-8 h-8 text-blue-400 mx-auto mb-3" />
              <div className="text-2xl font-bold text-white mb-1">94%</div>
              <div className="text-sm text-gray-400">Policy Compliance</div>
              <div className="text-xs text-green-400 mt-1">+2% improvement</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 text-center">
              <Brain className="w-8 h-8 text-purple-400 mx-auto mb-3" />
              <div className="text-2xl font-bold text-white mb-1">77</div>
              <div className="text-sm text-gray-400">AI Interventions</div>
              <div className="text-xs text-green-400 mt-1">£83K saved</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 text-center">
              <FileText className="w-8 h-8 text-yellow-400 mx-auto mb-3" />
              <div className="text-2xl font-bold text-white mb-1">100%</div>
              <div className="text-sm text-gray-400">Audit Trail</div>
              <div className="text-xs text-green-400 mt-1">Complete coverage</div>
            </div>
          </div>
        </div>
      )}

      {/* AI Enablement Section */}
      {activeSection === 'ai' && (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-white flex items-center space-x-3">
            <Brain className="w-6 h-6 text-cyan-400" />
            <span>AI Enablement & Augmentation</span>
          </h2>

          {/* AI Usage Breakdown */}
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-4">AI Usage Across Development Lifecycle</h3>
            <div className="space-y-4">
              {aiUsageData.map((category, index) => (
                <div key={index} className="bg-gray-700 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-white font-medium">{category.category}</h4>
                    <div className="flex items-center space-x-4">
                      <span className="text-cyan-400 font-bold">{category.ai_assisted}% AI-assisted</span>
                      <span className="text-green-400 font-bold">{category.time_saved}h saved</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <div className="text-gray-400 text-sm">AI Assistance</div>
                      <div className="w-full bg-gray-600 rounded-full h-2 mt-1">
                        <div 
                          className="bg-cyan-500 h-2 rounded-full" 
                          style={{ width: `${category.ai_assisted}%` }}
                        ></div>
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-400 text-sm">Time Saved</div>
                      <div className="text-green-400 font-medium">{category.time_saved} hours</div>
                    </div>
                    <div>
                      <div className="text-gray-400 text-sm">Accuracy</div>
                      <div className={`font-medium ${category.accuracy >= 90 ? 'text-green-400' : 'text-yellow-400'}`}>
                        {category.accuracy}%
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* AI Impact Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 text-center">
              <Brain className="w-8 h-8 text-cyan-400 mx-auto mb-3" />
              <div className="text-2xl font-bold text-white mb-1">68%</div>
              <div className="text-sm text-gray-400">Tasks AI-Assisted</div>
              <div className="text-xs text-cyan-400 mt-1">Across all categories</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 text-center">
              <Clock className="w-8 h-8 text-green-400 mx-auto mb-3" />
              <div className="text-2xl font-bold text-white mb-1">145h</div>
              <div className="text-sm text-gray-400">Time Saved</div>
              <div className="text-xs text-green-400 mt-1">Per sprint</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 text-center">
              <Target className="w-8 h-8 text-purple-400 mx-auto mb-3" />
              <div className="text-2xl font-bold text-white mb-1">90%</div>
              <div className="text-sm text-gray-400">Average Accuracy</div>
              <div className="text-xs text-purple-400 mt-1">AI suggestions</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 text-center">
              <Zap className="w-8 h-8 text-yellow-400 mx-auto mb-3" />
              <div className="text-2xl font-bold text-white mb-1">25%</div>
              <div className="text-sm text-gray-400">Underutilized Areas</div>
              <div className="text-xs text-yellow-400 mt-1">Opportunity for growth</div>
            </div>
          </div>
        </div>
      )}

      {/* Impact & Learning Section */}
      {activeSection === 'learning' && (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-white flex items-center space-x-3">
            <Lightbulb className="w-6 h-6 text-orange-400" />
            <span>Impact & Continuous Improvement</span>
          </h2>

          {/* Learning Metrics */}
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-4">Learning Velocity & Hypothesis Testing</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={learningMetricsData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="quarter" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1F2937',
                    border: '1px solid #374151',
                    borderRadius: '8px',
                    color: '#F9FAFB'
                  }}
                />
                <Bar dataKey="hypotheses_tested" fill="#3B82F6" name="Hypotheses Tested" />
                <Bar dataKey="success_rate" fill="#10B981" name="Success Rate %" />
                <Bar dataKey="learning_velocity" fill="#8B5CF6" name="Learning Velocity" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Continuous Improvement Actions */}
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-4">Continuous Improvement Actions</h3>
            <div className="space-y-4">
              {continuousImprovementData.map((improvement, index) => (
                <div key={index} className="bg-gray-700 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-white font-medium">{improvement.improvement}</h4>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        improvement.status === 'completed' ? 'bg-green-900 text-green-300' :
                        improvement.status === 'in_progress' ? 'bg-blue-900 text-blue-300' :
                        'bg-gray-600 text-gray-300'
                      }`}>
                        {improvement.status.replace('_', ' ')}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        improvement.impact === 'high' ? 'bg-red-900 text-red-300' : 'bg-yellow-900 text-yellow-300'
                      }`}>
                        {improvement.impact} impact
                      </span>
                    </div>
                  </div>
                  <div className="text-sm text-gray-400">
                    Time saved: {improvement.time_saved} hours per sprint
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Impact Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 text-center">
              <Lightbulb className="w-8 h-8 text-orange-400 mx-auto mb-3" />
              <div className="text-2xl font-bold text-white mb-1">35</div>
              <div className="text-sm text-gray-400">Hypotheses Tested</div>
              <div className="text-xs text-orange-400 mt-1">This quarter</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 text-center">
              <CheckCircle className="w-8 h-8 text-green-400 mx-auto mb-3" />
              <div className="text-2xl font-bold text-white mb-1">76%</div>
              <div className="text-sm text-gray-400">Success Rate</div>
              <div className="text-xs text-green-400 mt-1">+8% improvement</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 text-center">
              <TrendingUp className="w-8 h-8 text-blue-400 mx-auto mb-3" />
              <div className="text-2xl font-bold text-white mb-1">85%</div>
              <div className="text-sm text-gray-400">Learning Velocity</div>
              <div className="text-xs text-blue-400 mt-1">Accelerating</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EngineeringDashboard;